import React, { useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from "firebase/firestore";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import app from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import auth from "./auth";
import VehicleCard2 from "./VehicleCard";
import VehicleCardAlt from "./VehicleCardAlt";

// Background image
import back7 from "./back1.png";

// Colors for the Pie Chart
const COLORS = ["#007bff", "#28a745", "#ffc107", "#17a2b8", "#dc3545", "#6610f2"];

const InvestmentsPage = () => {
  const db = getFirestore(app);

  const [user, setUser] = useState(null);

  // Merged investment + car data
  const [investments, setInvestments] = useState([]);

  // Graph data for revenue over time
  const [graphData, setGraphData] = useState([]);

  // Summaries
  const [totalPortfolioValue, setTotalPortfolioValue] = useState(0);
  const [averageROI, setAverageROI] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);

  // Top vehicles by ROI
  const [topVehicles, setTopVehicles] = useState([]);

  // Map location data
  const [mapLocations, setMapLocations] = useState([]);

  // For the investment distribution pie chart
  const [investmentDistribution, setInvestmentDistribution] = useState([
    { name: "Commuter Vehicles", value: 0 },
    { name: "SuperCars", value: 0 },
    { name: "HyperCars", value: 0 },
    { name: "Trucks", value: 0 },
    { name: "RVs", value: 0 },
    { name: "Special Vehicles", value: 0 },
  ]);

  // Toggle view mode: "grid" or "list"
  const [viewMode, setViewMode] = useState("grid");

  // Listen for user auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Main fetch logic once user is known
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        // Initialize distribution array each fetch
        const localDist = [
          { name: "Commuter Vehicles", value: 0 },
          { name: "SuperCars", value: 0 },
          { name: "HyperCars", value: 0 },
          { name: "Trucks", value: 0 },
          { name: "RVs", value: 0 },
          { name: "Special Vehicles", value: 0 },
        ];

        // 1. Query investments collection for this user
        const invRef = collection(db, "investments");
        const invQ = query(invRef, where("userID", "==", user.uid));
        const invSnap = await getDocs(invQ);

        // 2. For each investment doc, fetch associated car doc
        const fetchedData = await Promise.all(
          invSnap.docs.map(async (invDoc) => {
            const invData = invDoc.data();
            if (!invData.carID) return null;

            const carRef = doc(db, "cars", invData.carID);
            const carSnap = await getDoc(carRef);
            if (!carSnap.exists()) return null;

            const carData = carSnap.data() || {};

            return {
              carID: invData.carID,
              amount: invData.amount || 0, // The user’s invested amount
              carName: carData.carName || "Unnamed",
              carType: carData.carType || "Special Vehicles",
              averageROI: carData.averageROI || 0,
              totalValue: carData.totalValue || 0,
              moneyRaised: carData.moneyRaised || 0,
              revenueValues: carData.revenueValues || [],
              location: carData.location || "Unknown",
              latitude: carData.latitude || 0,
              longitude: carData.longitude || 0,
              pictures: carData.pictures || [],
              tags: carData.tags || [],
            };
          })
        );

        // Filter out any null or undefined
        const mergedInvestments = fetchedData.filter(Boolean);

        // Summaries
        let portfolioValue = 0;
        let weightedROISum = 0;
        let revenueSum = 0;

        // Map data
        const mapData = {};

        // Accumulate
        mergedInvestments.forEach((inv) => {
          const { amount, totalValue, revenueValues, averageROI, carType } = inv;

          // Sum user’s portfolio value
          portfolioValue += amount;

          // Weighted ROI
          weightedROISum += averageROI * amount;

          // If the car has revenue history, accumulate partial revenue
          if (Array.isArray(revenueValues) && revenueValues.length > 0 && totalValue > 0) {
            const sumRev = revenueValues.reduce((acc, v) => acc + (v || 0), 0);
            // partial ownership
            revenueSum += sumRev * (amount / totalValue);
          }

          // Distribution
          if (carType) {
            const lowerType = carType.toLowerCase();
            let matched = false;
            for (let d of localDist) {
              // Basic check by name
              if (lowerType.includes("commuter") && d.name === "Commuter Vehicles") {
                d.value += amount;
                matched = true;
                break;
              }
              if (lowerType.includes("super") && d.name === "SuperCars") {
                d.value += amount;
                matched = true;
                break;
              }
              if (lowerType.includes("hyper") && d.name === "HyperCars") {
                d.value += amount;
                matched = true;
                break;
              }
              if (lowerType.includes("truck") && d.name === "Trucks") {
                d.value += amount;
                matched = true;
                break;
              }
              if (lowerType.includes("rv") && d.name === "RVs") {
                d.value += amount;
                matched = true;
                break;
              }
            }
            if (!matched) {
              const special = localDist.find((x) => x.name === "Special Vehicles");
              if (special) special.value += amount;
            }
          }

          // Map data
          if (inv.latitude && inv.longitude) {
            mapData[inv.carID] = {
              lat: inv.latitude,
              lng: inv.longitude,
              name: inv.carName,
            };
          }
        });

        setInvestments(mergedInvestments);
        setTotalPortfolioValue(portfolioValue);
        setAverageROI(portfolioValue > 0 ? weightedROISum / portfolioValue : 0);
        setTotalRevenue(revenueSum);
        setMapLocations(Object.values(mapData));
        setInvestmentDistribution(localDist);

        // Top vehicles by ROI
        const sortedByROI = [...mergedInvestments].sort((a, b) => b.averageROI - a.averageROI);
        setTopVehicles(sortedByROI.slice(0, 3));

        // Build monthly revenue graph data (last 6 months). 
        // If you store payouts separately, adapt. 
        // For now, let's just pretend aggregated from revenueValues
        // This is hypothetical: you'd normally query a "payouts" or "revenue" collection.
        const now = new Date();
        const monthsArr = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const label = d.toLocaleString("default", { month: "short", year: "numeric" });
          monthsArr.push(label);
        }
        // We'll pretend each month has 0 for now or you can adapt logic to sum partial revenue
        const finalGraphData = monthsArr.map((m) => ({ month: m, revenue: 0 }));
        // Add real logic if you have monthly data
        setGraphData(finalGraphData);

      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [user]);

  return (
    <div
      style={{
        backgroundImage: `url(${back7})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        minHeight: "100vh",
        fontFamily: "'Roboto', sans-serif",
        color: "white",
        padding: "20px",
      }}
    >
      <h1 style={{ textAlign: "center", fontSize: "2.5rem" }}>My Portfolio</h1>

      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          backgroundColor: "rgba(0,0,0,0.3)",
          padding: "20px",
          borderRadius: "10px",
          marginBottom: "20px",
          textAlign: "center",
          fontSize: "1rem"
        }}
      >
        <div>
          <h3>Portfolio Value</h3>
          <p style={{ fontSize: "1.5em", fontWeight: "bold" }}>
            ${totalPortfolioValue.toLocaleString()}
          </p>
        </div>
        <div>
          <h3>Average ROI</h3>
          <p style={{ fontSize: "1.5em", fontWeight: "bold" }}>
            {averageROI.toFixed(2)}%
          </p>
        </div>
        <div>
          <h3>Total Revenue</h3>
          <p style={{ fontSize: "1.5em", fontWeight: "bold" }}>
            ${totalRevenue.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Vehicles Section */}
      <div
        style={{
          marginBottom: "20px",
          padding: "20px",
          backgroundColor: "rgba(0,0,0,0.3)",
          borderRadius: "10px",
        }}
      >
        <h2 style={{ textAlign: "center" }}>My Investments</h2>
        <div style={{ textAlign: "right" }}>
          <button
            onClick={() => setViewMode((prev) => (prev === "grid" ? "list" : "grid"))}
            style={{
              padding: "10px",
              fontSize: "1.2em",
              borderRadius: "5px",
              cursor: "pointer",
              backgroundColor: "#007bff",
              color: "#fff",
              border: "none",
              marginBottom: "10px",
            }}
          >
            {viewMode === "grid" ? "☰" : "☷"}
          </button>
        </div>

        {investments.length === 0 ? (
          <p style={{ textAlign: "center" }}>
            You don’t own any vehicles. <a href="/marketplace" style={{ color: "#00c9ff" }}>
              Browse Marketplace
            </a>
          </p>
        ) : (
          <>
            {viewMode === "grid" ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "20px",
                  justifyContent: "center",
                }}
              >
                {investments.map((inv) => (
                  <VehicleCard2
                    key={inv.carID}
                    carID={inv.carID}
                    carName={inv.carName}
                    tags={inv.tags || []}
                    image={inv.pictures?.[0]}
                    totalReceived={inv.moneyRaised}
                    roi={inv.averageROI}
                    totalValue={inv.totalValue}
                    location={inv.location}
                    progress={inv.totalValue ? (inv.moneyRaised / inv.totalValue) * 100 : 0}
                  />
                ))}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {investments.map((inv) => (
                  <VehicleCardAlt
                    key={inv.carID}
                    carID={inv.carID}
                    carName={inv.carName}
                    tags={inv.tags || []}
                    image={inv.pictures?.[0]}
                    totalReceived={inv.moneyRaised}
                    roi={inv.averageROI}
                    totalValue={inv.totalValue}
                    location={inv.location}
                    progress={inv.totalValue ? (inv.moneyRaised / inv.totalValue) * 100 : 0}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Example Graph: Revenue Over Time */}
      <div
        style={{
          marginBottom: "20px",
          padding: "20px",
          backgroundColor: "rgba(0,0,0,0.3)",
          borderRadius: "10px",
        }}
      >
        <h2 style={{ textAlign: "center" }}>Revenue Over Time</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
            <XAxis dataKey="month" stroke="#ccc" />
            <YAxis stroke="#ccc" />
            <Tooltip />
            <Line type="monotone" dataKey="revenue" stroke="#007bff" strokeWidth={3} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Example: Pie Chart for Distribution */}
      <div
        style={{
          marginBottom: "20px",
          padding: "20px",
          backgroundColor: "rgba(0,0,0,0.3)",
          borderRadius: "10px",
        }}
      >
        <h2 style={{ textAlign: "center" }}>Investment Distribution</h2>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={investmentDistribution}
              dataKey="value"
              nameKey="name"
              outerRadius={100}
              fill="#007bff"
            >
              {investmentDistribution.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Legend />
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Example: Top Vehicles */}
      <div
        style={{
          marginBottom: "20px",
          padding: "20px",
          backgroundColor: "rgba(0,0,0,0.3)",
          borderRadius: "10px",
        }}
      >
        <h2 style={{ textAlign: "center" }}>Top-Performing Vehicles</h2>
        {topVehicles.length === 0 ? (
          <p style={{ textAlign: "center" }}>No vehicles to display.</p>
        ) : (
          topVehicles.map((vehicle) => (
            <div key={vehicle.carID} style={{ marginBottom: "10px" }}>
              <p>
                <strong>{vehicle.carName}</strong> - ROI: {vehicle.averageROI}% - 
                Invested Amount: ${vehicle.amount?.toLocaleString() || 0}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Example: Map Section */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "rgba(0,0,0,0.3)",
          borderRadius: "10px",
        }}
      >
        <h2 style={{ textAlign: "center" }}>Vehicle Locations</h2>
        <MapContainer center={[37.7749, -122.4194]} zoom={4} style={{ height: "400px" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {mapLocations.map((loc, idx) => (
            <Marker key={idx} position={[loc.lat, loc.lng]}>
              <Popup>{loc.name}</Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default InvestmentsPage;
